import React, { useLayoutEffect, useRef, useState } from "react";

// Lightweight scroll-reveal wrapper using IntersectionObserver.
// Adds smooth fade + slight slide-up when the element enters the viewport.
export default function Reveal({ children, delay = 0, className = "", as: As = "div" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect users who prefer reduced motion — show immediately, no animation
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") { setShown(true); return; }
    // Already in the viewport at mount (above-the-fold hero content): paint it
    // immediately instead of starting from opacity 0 — keeps the LCP element
    // from being delayed by the observer callback + fade-in.
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) { setShown(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <As
      ref={ref}
      className={className}
      style={{
        transition: "opacity 700ms cubic-bezier(0.16, 1, 0.3, 1), transform 700ms cubic-bezier(0.16, 1, 0.3, 1)",
        transitionDelay: `${delay}ms`,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(24px)",
        willChange: "opacity, transform",
      }}
    >
      {children}
    </As>
  );
}
